//  ----------      START   IMPORT  BLOCK       ----------
import { Row, Col, Form, Button } from "react-bootstrap";
import { FaPlus, FaSmoking, FaCapsules, FaGlassMartiniAlt } from "react-icons/fa";
// Components
import HabitDetailCard from "./Widgets/HabitDetailCard";
// Context
import { useLanguage } from "../../../../../API/Context/LanguageContext";
// Config
import { VocabProfileOnboarding as Vocab } from "../../../../../ConfigFiles/multilingual/VocabProfileOnboarding";
import { 
  SUBSTANCES_CATEGORY_MAP,
  ALCOHOL_BEHAVIOR_MAP, 
  SMOKING_BEHAVIOR_MAP, 
  OTHER_SUBSTANCE_BEHAVIOR_MAP 
} from "../../../../../ConfigFiles/maps/substance_map";
import { HABIT_FREQUENCY_MAP } from "../../../../../ConfigFiles/maps/habit_frequency_map";
import { SubstancesCategory } from "../../../../../Types/Enums/SubstancesEnums";
//  ----------      END   IMPORT  BLOCK       ----------

const StepSubstanceUse = ({ data, update }) => {
    const { lan } = useLanguage();

    // --- Category Groups ---
    const SMOKING_CATEGORIES = [
        SubstancesCategory.TOBACCO, 
        SubstancesCategory.VAPE, 
        SubstancesCategory.CANNABIS
    ];

    const OTHER_CATEGORIES = [
        SubstancesCategory.STIMULANTS,
        SubstancesCategory.OPIOIDS,
        SubstancesCategory.SEDATIVES,
        SubstancesCategory.HALLUCINOGENS,
        SubstancesCategory.INHALANTS,
        SubstancesCategory.OTHER
    ];

    // --- Core Logic ---
    const updateHabit = (masterIndex, field, value) => {
        const newList = [...data.substances.substances_detail];
        
        // Only keep numeric parsing for true numerical quantities
        const isNumeric = ['quantity'].includes(field);
        
        let processedValue = value;

        if (isNumeric) {
            processedValue = value === "" ? "" : parseInt(value);
        } else if (field === 'year_start' || field === 'year_end') {
            processedValue = value.replace(/\D/g, ""); 
        }

        newList[masterIndex][field] = processedValue;
        update({ substances: { ...data.substances, substances_detail: newList } });
    };

    const addHabit = (categoryGroup) => {
        const usedCategories = data.substances.substances_detail.map(h => h.substance);
        
        let targetPool = [];
        if (categoryGroup === 'alcohol') targetPool = [SubstancesCategory.ALCOHOL];
        if (categoryGroup === 'smoking') targetPool = SMOKING_CATEGORIES;
        if (categoryGroup === 'other') targetPool = OTHER_CATEGORIES;

        const nextCategory = targetPool.find(cat => !usedCategories.includes(cat));

        if (nextCategory) {
            const newItem = {
                substance: nextCategory, // String Enum Value
                behavior_type: "",
                year_start: "",
                year_end: "",
                frequency: "",     
                quantity: ""       
            };
            update({ 
                substances: { 
                    ...data.substances, 
                    has_history: true,
                    substances_detail: [...data.substances.substances_detail, newItem] 
                } 
            });
        }
    };

    const removeHabit = (masterIndex) => {
        const newList = data.substances.substances_detail.filter((_, i) => i !== masterIndex);
        update({ substances: { ...data.substances, substances_detail: newList, has_history: newList.length > 0 } });
    };

    // --- Dynamic Category Option Filtering ---
    const getFilteredCategoryOptions = (allowedCategoryKeys, currentSubstance) => {
        const usedCategories = data.substances.substances_detail.map(h => h.substance);
        
        return SUBSTANCES_CATEGORY_MAP
            .filter(opt => allowedCategoryKeys.includes(opt.id))
            .filter(opt => !usedCategories.includes(opt.id) || opt.id === currentSubstance)
            .map(opt => ({ value: opt.id, label: opt.labels[lan] }));
    };

    const masterList = data.substances.substances_detail.map((item, index) => ({ ...item, masterIndex: index }));

    const alcoholItems = masterList.filter(h => h.substance === SubstancesCategory.ALCOHOL);
    const smokingItems = masterList.filter(h => SMOKING_CATEGORIES.includes(h.substance));
    const otherItems = masterList.filter(h => OTHER_CATEGORIES.includes(h.substance));

    return (
        <div className="animate-in">
            <h4 className="mb-4 border-start border-primary border-4 ps-3">{Vocab.substances_section_title[lan]}</h4>

            {/* --- ALCOHOL --- */}
            <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
                <h6 className="fw-bold mb-3"><FaGlassMartiniAlt className="me-2 text-info"/>{Vocab.alcohol_consumption[lan]}</h6>
                {alcoholItems.map((item) => (
                    <HabitDetailCard 
                        key={item.masterIndex} item={item} index={item.masterIndex}
                        isFixedType behaviorOptions={ALCOHOL_BEHAVIOR_MAP}
                        onUpdate={updateHabit} onRemove={removeHabit}
                    />
                ))}
                {alcoholItems.length === 0 && (
                    <Button variant="outline-info" size="sm" onClick={() => addHabit('alcohol')}>
                        <FaPlus size={10} className="me-1"/> {Vocab.add_habit[lan]}
                    </Button>
                )}
            </div>

            {/* --- SMOKING --- */}
            <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
                <h6 className="fw-bold mb-3"><FaSmoking className="me-2 text-secondary"/>{Vocab.smoking[lan]}</h6>
                {smokingItems.map((item) => (
                    <HabitDetailCard 
                        key={item.masterIndex} item={item} index={item.masterIndex}
                        typeOptions={getFilteredCategoryOptions(SMOKING_CATEGORIES, item.substance)}
                        behaviorOptions={SMOKING_BEHAVIOR_MAP}
                        onUpdate={updateHabit} onRemove={removeHabit}
                    />
                ))}
                <Button 
                    variant="outline-secondary" size="sm" 
                    onClick={() => addHabit('smoking')}
                    disabled={smokingItems.length >= SMOKING_CATEGORIES.length}
                >
                    <FaPlus size={10} className="me-1"/> {Vocab.add_habit[lan]}
                </Button>
            </div>

            {/* --- OTHERS --- */}
            <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
                <h6 className="fw-bold mb-3"><FaCapsules className="me-2 text-warning"/>{Vocab.substances_use[lan]}</h6>
                {otherItems.map((item) => (
                    <HabitDetailCard 
                        key={item.masterIndex} item={item} index={item.masterIndex}
                        typeOptions={getFilteredCategoryOptions(OTHER_CATEGORIES, item.substance)}
                        behaviorOptions={OTHER_SUBSTANCE_BEHAVIOR_MAP}
                        onUpdate={updateHabit} onRemove={removeHabit}
                    />
                ))}
                <Button 
                    variant="outline-warning" size="sm" 
                    onClick={() => addHabit('other')}
                    disabled={otherItems.length >= OTHER_CATEGORIES.length}
                >
                    <FaPlus size={10} className="me-1"/> {Vocab.add_substance[lan]}
                </Button>
            </div>
        </div>
    );
};

export default StepSubstanceUse;